import { Outlet, useLocation } from "react-router";
import { TemplateProvider } from "../shared/TemplateContext";
import { useAuth } from "../shared/AuthContext";
import { useState, useRef, useEffect, useCallback } from "react";
import { Lock, Eye, EyeOff, AlertCircle, Pencil, Loader2 } from "lucide-react";
import { apiFetch } from "../../utils/apiClient";

/**
 * SECURITY: Template access password is verified SERVER-SIDE via /auth/verify-access.
 * No hardcoded passwords in client code.
 */
const UNLOCK_KEY = "tpl_access_unlocked";

function isSessionUnlocked(): boolean {
  try {
    return sessionStorage.getItem(UNLOCK_KEY) === "1";
  } catch {
    return false;
  }
}

function setSessionUnlocked() {
  try {
    sessionStorage.setItem(UNLOCK_KEY, "1");
  } catch {
    // ignore
  }
}

/**
 * TemplateLayout — wraps all template site pages.
 * Shows a password gate before allowing any access (view + edit).
 * Password verification happens server-side.
 * Provides TemplateContext (templateId from URL params).
 *
 * IMPORTANT: When loaded and the user is NOT logged into the dashboard,
 * saves the current path as returnUrl so that after dashboard login
 * the user is redirected back here instead of staying on the dashboard.
 */
export function TemplateLayout() {
  const [unlocked, setUnlocked] = useState(isSessionUnlocked);
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const location = useLocation();
  const { user, setReturnUrl } = useAuth();

  // Save template path as returnUrl when user is not logged into dashboard.
  useEffect(() => {
    if (!user) {
      const currentPath = location.pathname + location.search;
      setReturnUrl(currentPath);
    }
  }, [user, location.pathname, location.search, setReturnUrl]);

  useEffect(() => {
    if (!unlocked) {
      const t = setTimeout(() => inputRef.current?.focus(), 120);
      return () => clearTimeout(t);
    }
  }, [unlocked]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!password.trim() || verifying) return;

      setVerifying(true);
      setError("");

      try {
        const res = await apiFetch("/auth/verify-access", {
          method: "POST",
          body: JSON.stringify({ password: password.trim() }),
        });

        if (res.valid) {
          setSessionUnlocked();
          setUnlocked(true);
          setPassword("");
          setError("");
        } else {
          setError("\u05E1\u05D9\u05E1\u05DE\u05D4 \u05E9\u05D2\u05D5\u05D9\u05D4");
          setPassword("");
        }
      } catch {
        setError("\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D0\u05D9\u05DE\u05D5\u05EA");
        setPassword("");
      } finally {
        setVerifying(false);
      }
    },
    [password, verifying],
  );

  /* ---- Unlocked — render the template normally ---- */
  if (unlocked) {
    return (
      <TemplateProvider>
        <Outlet />
      </TemplateProvider>
    );
  }

  /* ---- Locked — password gate ---- */
  return (
    <div
      dir="rtl"
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        fontFamily: "var(--font-family)",
        background:
          "linear-gradient(135deg, #001C32 0%, #041f5e 30%, #052b7a 60%, #1a4494 100%)",
      }}
    >
      {/* Decorative blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[10%] right-[15%] w-72 h-72 bg-zap-light/10 rounded-full blur-3xl" />
        <div className="absolute bottom-[20%] left-[10%] w-96 h-96 bg-zap/8 rounded-full blur-3xl" />
        <div className="absolute top-[50%] left-[50%] w-64 h-64 bg-zap-glow/6 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      </div>

      <div className="relative w-full max-w-sm">
        {/* Icon + title */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-zap-light to-zap-dark rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-zap/25">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl text-white mb-1" style={{ fontWeight: 700 }}>
            {"\u05EA\u05D5\u05DB\u05DF \u05DE\u05D5\u05D2\u05DF"}
          </h1>
          <p className="text-zap-pale/60 text-sm">
            {"\u05D4\u05D6\u05D9\u05E0\u05D5 \u05E1\u05D9\u05E1\u05DE\u05D4 \u05DB\u05D3\u05D9 \u05DC\u05E6\u05E4\u05D5\u05EA \u05D5\u05DC\u05E2\u05E8\u05D5\u05DA \u05D0\u05EA \u05D4\u05D0\u05EA\u05E8"}
          </p>
        </div>

        {/* Card */}
        <div className="bg-white/[0.07] backdrop-blur-xl border border-white/[0.12] rounded-2xl p-6 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Password field */}
            <div>
              <label
                className="text-xs text-zap-pale/70 mb-2 block"
                style={{ fontWeight: 500 }}
              >
                {"\u05E1\u05D9\u05E1\u05DE\u05D4"}
              </label>
              <div className="relative">
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zap-glow/40" />
                <input
                  ref={inputRef}
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError("");
                  }}
                  placeholder={"\u2022\u2022\u2022"}
                  className={`w-full pr-10 pl-10 py-3 rounded-xl text-sm outline-none transition-all ${
                    error
                      ? "bg-red-500/10 border-2 border-red-400/50 text-white placeholder:text-red-300/40"
                      : "bg-white/[0.08] border-2 border-transparent text-white placeholder:text-zap-pale/30 focus:border-zap-glow/50 focus:bg-white/[0.12]"
                  }`}
                  dir="ltr"
                  autoComplete="off"
                  disabled={verifying}
                />
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowPw(!showPw)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") setShowPw(!showPw);
                  }}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-zap-glow/40 hover:text-zap-pale cursor-pointer transition-colors"
                >
                  {showPw ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </span>
              </div>
              {error && (
                <p className="text-xs text-red-400 mt-2 flex items-center gap-1.5">
                  <AlertCircle className="w-3 h-3" />
                  {error}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={!password.trim() || verifying}
              className="w-full py-3 bg-gradient-to-l from-zap-light to-zap text-white rounded-xl text-sm hover:from-zap hover:to-zap-dark transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-zap/20"
              style={{ fontWeight: 600 }}
            >
              {verifying ? (
                <Loader2 className="w-4 h-4 animate-spin mx-auto" />
              ) : (
                "\u05DB\u05E0\u05D9\u05E1\u05D4"
              )}
            </button>
          </form>
        </div>

        {/* Subtle hint */}
        <div className="flex items-center justify-center gap-1.5 mt-6 text-zap-pale/25 text-[11px]">
          <Pencil className="w-3 h-3" />
          <span>{"\u05D0\u05D7\u05E8\u05D9 \u05DB\u05E0\u05D9\u05E1\u05D4 \u05EA\u05D5\u05DB\u05DC\u05D5 \u05D2\u05DD \u05DC\u05E2\u05E8\u05D5\u05DA \u05D0\u05EA \u05D4\u05EA\u05D5\u05DB\u05DF"}</span>
        </div>
      </div>
    </div>
  );
}
